import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Button, TextField, InputAdornment, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PaymentIcon from '@mui/icons-material/Payment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { IPagoInmuebleListItem, STATUS_LABELS, StatusPago } from '../../models/pagosInmuebles';

interface ListPagosInmueblesProps {
    listado: IPagoInmuebleListItem[];
    loading: boolean;
    onSelectCasa: (casa: IPagoInmuebleListItem) => void;
    onRegistrarPago: (casa: IPagoInmuebleListItem) => void;
    onCargarPagos: () => void;
}

const STATUS_COLORS: Record<StatusPago, { bg: string; color: string }> = {
    AL_DIA: { bg: '#e8f5e9', color: '#2e7d32' },
    PENDIENTE: { bg: '#fff8e1', color: '#f9a825' },
    ATRASADO: { bg: '#fdecea', color: '#c62828' },
    SALDO_FAVOR: { bg: '#e3f2fd', color: '#1565c0' },
};

export const ListPagosInmuebles = ({ listado, loading, onSelectCasa, onRegistrarPago, onCargarPagos }: ListPagosInmueblesProps) => {
    const [filtro, setFiltro] = useState('');

    const listadoFiltrado = useMemo(() => {
        if (!filtro.trim()) return listado;
        const f = filtro.trim().toLowerCase();
        return listado.filter(item =>
            item.num_Casa?.toLowerCase().includes(f) || item.nombre?.toLowerCase().includes(f)
        );
    }, [listado, filtro]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', pb: 4 }}>
            <Box sx={{
                background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
                px: 3, py: 4, mb: 3,
            }}>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                    Pagos Inmuebles
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    Estado de cuenta y registro de pagos por inmueble
                </Typography>
            </Box>

            <Box sx={{ px: 2, display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Buscar por casa o nombre..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    sx={{ flex: 1, minWidth: 200, backgroundColor: '#fff', borderRadius: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#888' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={onCargarPagos}
                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#013467', color: '#013467' }}
                >
                    Cargar pagos
                </Button>
            </Box>

            <Box sx={{ px: 2 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && listadoFiltrado.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" mt={6}>
                        No hay inmuebles para mostrar.
                    </Typography>
                )}

                {!loading && listadoFiltrado.map(item => {
                    const statusStyle = STATUS_COLORS[item.status] ?? STATUS_COLORS.AL_DIA;
                    return (
                        <Box
                            key={item.casa}
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                mb: 2,
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(1,52,103,0.08)',
                                cursor: 'pointer',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(1,52,103,0.15)',
                                },
                            }}
                            onClick={() => onSelectCasa(item)}
                        >
                            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #013467, #FFCC00)' }} />

                            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: 2,
                                    backgroundColor: '#e8f0fb',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                }}>
                                    <HomeWorkIcon sx={{ color: '#013467', fontSize: 22 }} />
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#013467', lineHeight: 1.3, mb: 0.5 }}>
                                        {item.num_Casa} - {item.nombre}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#555' }}>
                                            Saldo: <strong>Q. {item.saldo.toFixed(2)}</strong>
                                        </Typography>
                                        <Chip
                                            label={STATUS_LABELS[item.status] ?? item.status}
                                            size="small"
                                            sx={{
                                                height: 20, fontSize: 10, fontWeight: 600,
                                                backgroundColor: statusStyle.bg, color: statusStyle.color,
                                            }}
                                        />
                                    </Box>
                                    <Button
                                        size="small"
                                        startIcon={<PaymentIcon sx={{ fontSize: '16px !important' }} />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRegistrarPago(item);
                                        }}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, fontSize: 12,
                                            color: '#fff', backgroundColor: '#013467', borderRadius: 1.5,
                                            px: 1.5, py: 0.5,
                                            '&:hover': { backgroundColor: '#0a4a8a' },
                                        }}
                                        variant="contained"
                                    >
                                        Registrar pago
                                    </Button>
                                </Box>

                                <ChevronRightIcon sx={{ color: '#bbb', flexShrink: 0, mt: 0.5 }} />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
